import { BlogLanguageSwitcher, type BlogLocale } from '@saas-pages/shared/public/blog'
import { Seo } from '@saas-pages/shared/public/seo'
import branding from '../../../branding.config'
import { seoFor } from '@/seo/routes'
import { getAllPosts } from '@/shared/blog/posts'
import { useLocale } from '@/shared/i18n'

const COPY = {
  ru: {
    title: 'Блог о записи аудио и видео',
    description: 'Практические разборы форматов, разрешений и локальной записи медиа в браузере.',
    breadcrumb: 'Блог',
    languageAria: 'Язык статей',
    localeName: { ru: 'Русский', en: 'English' },
  },
  en: {
    title: 'Browser audio and video guides',
    description: 'Practical guides to recording formats, permissions, and local media capture in a browser.',
    breadcrumb: 'Blog',
    languageAria: 'Article language',
    localeName: { ru: 'Русский', en: 'English' },
  },
} satisfies Record<BlogLocale, {
  title: string
  description: string
  breadcrumb: string
  languageAria: string
  localeName: Record<BlogLocale, string>
}>

function formatDate(iso: string, locale: BlogLocale): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString(locale === 'en' ? 'en-GB' : 'ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

export function BlogIndexPage() {
  const { locale, setLocale } = useLocale()
  const copy = COPY[locale]
  const posts = getAllPosts(locale)

  return (
    <>
      <Seo {...seoFor('blog')} ogImage={branding.ogImage} />

      <div className="mx-auto max-w-3xl">
        <nav aria-label="mediatik.ru" className="mb-6 text-sm text-muted-foreground">
          <a href="/" className="hover:text-foreground">mediatik.ru</a>
          <span className="mx-2">/</span>
          <span className="text-foreground">{copy.breadcrumb}</span>
        </nav>

        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-3xl font-bold tracking-normal sm:text-4xl">{copy.title}</h1>
          <BlogLanguageSwitcher
            currentLocale={locale}
            ariaLabel={copy.languageAria}
            labels={copy.localeName}
            onSelectLocale={setLocale}
          />
        </div>
        <p className="mb-10 leading-7 text-muted-foreground">{copy.description}</p>

        <ul className="space-y-6">
          {posts.map((post) => (
            <li key={post.slug}>
              <article className="rounded-lg border border-rose-200/80 bg-card p-6 shadow-sm transition-shadow hover:shadow-md dark:border-rose-900/60">
                <a href={`/blog/${post.slug}`} className="group block">
                  <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <time dateTime={post.date}>{formatDate(post.date, locale)}</time>
                    {post.tags.length > 0 && (
                      <>
                        <span aria-hidden>·</span>
                        <span>{post.tags.join(', ')}</span>
                      </>
                    )}
                  </div>
                  <h2 className="text-xl font-semibold tracking-normal group-hover:text-primary sm:text-2xl">
                    {post.title}
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">{post.description}</p>
                </a>
              </article>
            </li>
          ))}
        </ul>
      </div>
    </>
  )
}
